import {computed, ComputedRef, onScopeDispose, Ref, ref, watch} from 'vue'
import {useEventListener} from '@vueuse/core'

export type TooltipTriggerType = 'hover' | 'click' | 'manual'

interface ITooltipTriggerOptions {
  trigger: () => TooltipTriggerType
  visible: () => boolean | undefined
  isDisabled: () => boolean
  canShow?: () => boolean
}

interface IHoveredTooltip {
  setShown: (isShown: boolean) => void
}

const TOOLTIP_TRIGGER_EVENTS = ['pointerenter', 'pointerleave', 'pointerdown', 'pointerup', 'pointercancel', 'focus', 'blur', 'click']

let tooltipIdCounter = 0
const hoveredTooltips: IHoveredTooltip[] = []

function showInnermostHoveredTooltip() {
  const innermostIndex = hoveredTooltips.length - 1
  hoveredTooltips.forEach((hoveredTooltip, index) => {
    hoveredTooltip.setShown(index === innermostIndex)
  })
}

export function useTooltipTrigger(referenceRef: Ref<HTMLElement | null>, options: ITooltipTriggerOptions): {
  isShown: ComputedRef<boolean>
  popperId: string
} {
  tooltipIdCounter += 1
  const popperId = `tooltip-${tooltipIdCounter}`
  const isTriggered = ref(false)
  const hoveredTooltip: IHoveredTooltip = {
    setShown: (isShown) => {
      isTriggered.value = isShown
    },
  }

  const enter = () => {
    if(options.isDisabled() || (options.canShow && !options.canShow())) {
      return
    }
    if(!hoveredTooltips.includes(hoveredTooltip)) {
      hoveredTooltips.push(hoveredTooltip)
    }
    showInnermostHoveredTooltip()
  }

  const leave = () => {
    const index = hoveredTooltips.indexOf(hoveredTooltip)
    if(index !== -1) {
      hoveredTooltips.splice(index, 1)
    }
    isTriggered.value = false
    showInnermostHoveredTooltip()
  }

  const leaveAll = () => {
    for(const tooltip of hoveredTooltips.splice(0)) {
      tooltip.setShown(false)
    }
    isTriggered.value = false
  }

  const onHoverTriggerEvent = (event: Event) => {
    const isTouch = (event as PointerEvent).pointerType === 'touch'
    switch(event.type) {
    case 'pointerenter':
      if(!isTouch) {
        enter()
      }
      break
    case 'pointerleave':
      if(!isTouch) {
        leave()
      }
      break
    case 'pointerdown':
      if(isTouch) {
        enter()
      }
      break
    case 'pointerup':
    case 'pointercancel':
      if(isTouch) {
        leave()
      }
      break
    case 'focus':
      enter()
      break
    case 'blur':
      leave()
      break
    case 'click':
      leaveAll()
      break
    }
  }

  useEventListener(referenceRef, TOOLTIP_TRIGGER_EVENTS, (event: Event) => {
    const trigger = options.trigger()
    if(trigger === 'hover') {
      onHoverTriggerEvent(event)
      return
    }
    if(trigger === 'click' && event.type === 'click') {
      isTriggered.value = !isTriggered.value && !options.isDisabled()
    }
  })

  watch(() => options.isDisabled() || options.trigger(), () => {
    leave()
  })

  onScopeDispose(() => {
    leave()
  })

  const isShown = computed(() => {
    if(options.isDisabled()) {
      return false
    }
    if(options.trigger() === 'manual') {
      return Boolean(options.visible())
    }
    return isTriggered.value
  })

  return {
    isShown,
    popperId,
  }
}
